import React, { useRef, useState, useEffect } from 'react';
import { CircleOffIcon,Ruler, ZoomIn, Move, RotateCcw, Square, Download, Maximize2, Type, RefreshCw } from 'lucide-react';

export default function XrayViewerApp() {
  const canvasRef = useRef(null);
  const [images] = useState([
    'https://t4.ftcdn.net/jpg/01/97/03/19/240_F_197031952_MGjgPGxw58GG6vQvA8xZh2f015WISGPe.jpg',
    'https://as2.ftcdn.net/v2/jpg/02/75/61/61/1000_F_275616178_iT2MiEjMrdBwekVUKZlrk22MOgJ2tLXR.jpg',
    'https://as1.ftcdn.net/v2/jpg/02/68/83/70/1000_F_268837068_jTvqA9bGsbPoJwOkGVWA6Bu1BLpj5lpf.jpg'
  ]);
  const [currentImage, setCurrentImage] = useState(0);
  const [tool, setTool] = useState('WL');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [wl, setWl] = useState({ window: 256, level: 128 });
  const [drawing, setDrawing] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [textAnnotations, setTextAnnotations] = useState([]);
  const [textInput, setTextInput] = useState('');
  const imgRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = images[currentImage];
    img.onload = () => {
      imgRef.current = img;
      draw();
    };
  }, [currentImage, zoom, pan, rotation, wl, annotations, textAnnotations]);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(canvas.width / 2 + pan.x, canvas.height / 2 + pan.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(zoom, zoom);

    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = imgRef.current.width;
    tmpCanvas.height = imgRef.current.height;
    const tmpCtx = tmpCanvas.getContext('2d');
    tmpCtx.drawImage(imgRef.current, 0, 0);

    const imageData = tmpCtx.getImageData(0, 0, tmpCanvas.width, tmpCanvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const intensity = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      let val = ((intensity - wl.level + wl.window / 2) / wl.window) * 255;
      val = Math.max(0, Math.min(255, val));
      data[i] = data[i + 1] = data[i + 2] = val;
    }
    tmpCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(tmpCanvas, -imgRef.current.width / 2, -imgRef.current.height / 2);

    ctx.strokeStyle = 'red';
    ctx.lineWidth = 2 / zoom;
    annotations.forEach(a => ctx.strokeRect(a.x, a.y, a.w, a.h));

    ctx.fillStyle = 'yellow';
    ctx.font = `${14/zoom}px Arial`;
    textAnnotations.forEach(t => ctx.fillText(t.text, t.x, t.y));

    ctx.restore();
  };

  const getEventPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const handlePointerDown = (e) => {
    const pos = getEventPos(e);
    if (tool === 'Annotate') setDrawing({ x: pos.x, y: pos.y, w: 0, h: 0 });
    else if (tool === 'Text' && textInput.trim() !== '') {
      setTextAnnotations([...textAnnotations, { text: textInput, x: pos.x - canvasRef.current.width/2, y: pos.y - canvasRef.current.height/2 }]);
      setTextInput('');
    } else setDrawing(pos);
  };

  const handlePointerMove = (e) => {
    if (!drawing) return;
    const pos = getEventPos(e);
    const dx = pos.x - drawing.x;
    const dy = pos.y - drawing.y;

    if (tool === 'Pan') setPan({ x: pan.x + dx, y: pan.y + dy });
    else if (tool === 'Zoom') setZoom(Math.min(Math.max(zoom * (dy > 0 ? 1.02 : 0.98), 0.3), 5));
    else if (tool === 'WL') setWl({ window: Math.max(1, wl.window + dx), level: wl.level + dy });
    else if (tool === 'Annotate') setDrawing({ ...drawing, w: dx, h: dy });

    setDrawing(pos);
  };

  const handlePointerUp = () => {
    if (tool === 'Annotate' && drawing) setAnnotations([...annotations, drawing]);
    setDrawing(null);
  };

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setRotation(0);
    setWl({ window: 256, level: 128 });
    setAnnotations([]);
    setTextAnnotations([]);
  };

  const exportImage = () => {
    const merged = document.createElement('canvas');
    merged.width = canvasRef.current.width;
    merged.height = canvasRef.current.height;
    const ctx = merged.getContext('2d');
    ctx.drawImage(canvasRef.current, 0, 0);
    merged.toBlob((blob) => {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'xray_viewer_export.png';
      link.click();
    });
  };

  return (
    <div className="flex h-screen bg-gray-950 text-white select-none">
      {/* Thumbnail panel for desktop (left) or mobile (bottom) */}
      <div className="flex flex-col md:flex-row w-full h-full">
        <div className="flex md:flex-col flex-row overflow-auto md:w-24 md:h-full w-full h-24 bg-gray-900 p-2 border-r md:border-b border-gray-800 gap-2">
          {images.map((img, idx) => (
            <img key={idx} src={img} alt={`thumb-${idx}`} className={`w-16 h-16 object-cover rounded cursor-pointer ring-2 ${currentImage === idx ? 'ring-blue-500' : 'ring-gray-700'}`} onClick={() => setCurrentImage(idx)} />
          ))}
        </div>
        <div className="flex-1 flex flex-col">
          <div className="flex justify-center items-center gap-2 p-2 bg-gray-900 border-b border-gray-800">
            {[
              { id: 'WL', icon: <CircleOffIcon size={20} /> },
              { id: 'Pan', icon: <Move size={20} /> },
              { id: 'Zoom', icon: <ZoomIn size={20} /> },
              { id: 'Rotate', icon: <RotateCcw size={20} /> },
              { id: 'Annotate', icon: <Square size={20} /> },
              { id: 'Text', icon: <Type size={20} /> },
              { id: 'Ruler', icon: <Ruler size={20} /> },
              { id: 'Fit', icon: <Maximize2 size={20} /> },
              { id: 'Reset', icon: <RefreshCw size={20} /> },
              { id: 'Export', icon: <Download size={20} /> }
            ].map(btn => (
              <button key={btn.id} title={btn.id} onClick={() => {
                if (btn.id === 'Export') return exportImage();
                if (btn.id === 'Rotate') return setRotation(r => r + 90);
                if (btn.id === 'Fit') { setZoom(1); setPan({ x: 0, y: 0 }); return; }
                if (btn.id === 'Reset') { resetView(); return; }
                setTool(btn.id);
              }} className={`p-2 rounded-xl flex items-center justify-center ${tool === btn.id ? 'bg-blue-600 text-white' : 'bg-gray-800 hover:bg-gray-700 text-gray-300'} transition-colors`}>{btn.icon}</button>
            ))}
          </div>
          <input type="text" style={{display:"none"}} placeholder="Enter text" value={textInput} onChange={e => setTextInput(e.target.value)} className="m-2 p-1 text-black w-64" />
          <div className="flex-1 relative overflow-hidden" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onTouchStart={handlePointerDown} onTouchMove={handlePointerMove} onTouchEnd={handlePointerUp}>
            <canvas ref={canvasRef} className="w-full h-full touch-none" />
          </div>
        </div>
      </div>
    </div>
  );
}
